import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, doc, setDoc, getDoc, query, where, limit, getDocs, serverTimestamp, updateDoc, getDocs as getDocsQuery } from 'firebase/firestore';
import Layout from '../../components/ui/Layout';
import { Container, Card, Button, Spinner, Alert } from 'react-bootstrap';
import NotificationPermission from '../../components/ui/NotificationPermission';
import { isIOSOrMobileSafari } from '../../utils/browserUtils';
import { useAuth } from '../../components/context/AuthContext';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { createLogger } from '../../utils/logger';
import { buildInventoryDecrementFields } from '../../utils/inventoryPricing';
import jsPDF from 'jspdf';

const logger = createLogger('WipayResponsePage');

// HTTP endpoint for employee notifications (avoids CORS issues)
const EMPLOYEE_NOTIFICATION_FUNCTION_URL = process.env.REACT_APP_EMPLOYEE_NOTIFICATION_FUNCTION_URL || 
  'https://us-central1-pack-my-cart-dev.cloudfunctions.net/notifyEmployeesNewOrderHttp';

function parseQueryParams(search) {
  const params = new URLSearchParams(search);
  const obj = {};
  for (const [key, value] of params.entries()) {
    obj[key] = value;
  }
  return obj;
}

// Note: Status validation functions are now imported from constants/cartStatus.ts


export default function WipayResponsePage() {
  useAuth();
  const [saving, setSaving] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({});
  const [cartTransactionId, setCartTransactionId] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [cartRestored, setCartRestored] = useState(false);
  const [cartStatus, setCartStatus] = useState(null);
  const [invalidCartStatus, setInvalidCartStatus] = useState(false);
  const navigate = useNavigate();
  
  const confirmationFee = parseFloat(process.env.REACT_APP_CONFIRMATION_FEE) || 500;
  const paymentType = process.env.REACT_APP_PAYMENT_TYPE || 'wipay';
  const paymentGateway = process.env.REACT_APP_PAYMENT_GATEWAY || 'WiPay';
  
  // Feature flag for employee notifications (customer notifications are handled in EmployeePackOrderPage)
  const enableEmployeeNotifications = process.env.REACT_APP_ENABLE_EMPLOYEE_NOTIFICATIONS === 'true' || process.env.REACT_APP_ENABLE_EMPLOYEE_NOTIFICATIONS === '1';
  const paymentMethod = process.env.REACT_APP_PAYMENT_METHOD || 'Credit Card';

  // Function to generate and download receipt PDF
  const generateReceipt = () => {
    if (!transactionData) {
      logger.warn('Cannot generate receipt: transaction data not available');
      return;
    }

    // Determine if this is a full payment order (declare once at function scope)
    const isFullPaymentOrder = transactionData.paymentType === 'full' || params.payment_type === 'full';

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    let yPosition = margin;
    const footerSpaceNeeded = 50;

    const ensureSpaceForFooter = () => {
      if (yPosition + footerSpaceNeeded > pageHeight) {
        doc.addPage();
        yPosition = margin;
      }
    };

    // Helper function to add text with line wrapping
    const addText = (text, fontSize = 10, isBold = false, align = 'left') => {
      doc.setFontSize(fontSize);
      if (isBold) {
        doc.setFont(undefined, 'bold');
      } else {
        doc.setFont(undefined, 'normal');
      }
      
      const textWidth = pageWidth - (margin * 2);
      const lines = doc.splitTextToSize(text, textWidth);
      
      lines.forEach(line => {
        if (align === 'center') {
          doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
        } else if (align === 'right') {
          doc.text(line, pageWidth - margin, yPosition, { align: 'right' });
        } else {
          doc.text(line, margin, yPosition);
        }
        yPosition += fontSize * 0.4;
      });
    };

    // Helper function to add line separator
    const addLine = () => {
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;
    };

    // Header
    addText('PACK MY CART', 18, true, 'center');
    yPosition += 5;
    addText('Receipt', 14, true, 'center');
    yPosition += 10;

    // Order Information
    addText('ORDER INFORMATION', 12, true);
    yPosition += 5;
    addText(`Order ID: ${transactionData.cartInstanceId || params.order_id}`, 10);
    addText(`Transaction ID: ${transactionData.paymentGatewayTransactionId || params.transaction_id || 'N/A'}`, 10);
    addText(`Date: ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`, 10);
    addText(`Status: ${transactionData.status || params.status}`, 10);
    yPosition += 5;

    // Customer Information
    addText('CUSTOMER', 12, true);
    yPosition += 5;
    addText(transactionData.customerName || 'Unknown Customer', 10);
    yPosition += 5;

    // Hardware store information
    addText('SUPERMARKET', 12, true);
    yPosition += 5;
    addText(transactionData.supermarketName || 'Unknown hardware store', 10);
    yPosition += 5;

    // Payment Information
    addText('PAYMENT INFORMATION', 12, true);
    yPosition += 5;
    if (isFullPaymentOrder) {
      addText('*** FULL PAYMENT ORDER ***', 14, true, 'center');
      yPosition += 5;
      addText('(Confirmation fee collected as processing fee)', 10, false, 'center');
      yPosition += 5;
    }
    addText(`Payment Gateway: ${transactionData.paymentGateway || paymentGateway}`, 10);
    addText(`Payment Method: ${transactionData.paymentMethod || params.method || paymentMethod}`, 10);
    addText(`Currency: ${params.currency || 'JMD'}`, 10);
    addText(`Payment Type: ${isFullPaymentOrder ? 'Full Payment' : 'Confirmation Fee Only'}`, 10, true);
    yPosition += 5;

    // Delivery Information (if applicable)
    if (transactionData.deliveryOption && transactionData.deliveryOption !== 'pickup') {
      addText('DELIVERY INFORMATION', 12, true);
      yPosition += 5;
      addText(`Delivery Option: ${transactionData.deliveryOption}`, 10);
      if (transactionData.deliveryAddress) {
        addText(`Address: ${transactionData.deliveryAddress}`, 10);
      }
      if (transactionData.deliveryPhone) {
        addText(`Phone: ${transactionData.deliveryPhone}`, 10);
      }
      yPosition += 5;
    }

    // Items (if available)
    let itemsTotalBeforeDeals = 0;
    if (transactionData.cartItems && transactionData.cartItems.length > 0) {
      addText('ITEMS', 12, true);
      yPosition += 5;
      
      transactionData.cartItems.forEach(item => {
        const itemTotal = (Number(item.price) || 0) * (Number(item.quantity) || 0);
        itemsTotalBeforeDeals += itemTotal;
        addText(`${item.name || 'Item'} x${item.quantity || 0}`, 10);
        addText(`$${Number(itemTotal).toFixed(2)}`, 10, false, 'right');
        yPosition += 3;
      });
      yPosition += 5;
    }

    addLine();

    // Totals
    const orderSubtotal = Number(transactionData.orderSubtotal) || 0;
    const totalDealSavings = itemsTotalBeforeDeals - orderSubtotal;
    const confirmationFeeAmount = Number(transactionData.confirmationFee) || confirmationFee;
    
    if (totalDealSavings > 0.01) {
      addText(`Subtotal (before deals): $${itemsTotalBeforeDeals.toFixed(2)}`, 10, false, 'right');
      addText(`Deal Applied: -$${totalDealSavings.toFixed(2)}`, 10, false, 'right');
      yPosition += 2;
    }
    addText(`Subtotal: $${orderSubtotal.toFixed(2)}`, 10, false, 'right');
    if (isFullPaymentOrder) {
      addText(`Processing Fee (Confirmation Fee): $${confirmationFeeAmount.toFixed(2)}`, 10, false, 'right');
    } else {
      addText(`Confirmation Fee: $${confirmationFeeAmount.toFixed(2)}`, 10, false, 'right');
      addText(`Subtotal (Due at Pickup): $${orderSubtotal.toFixed(2)}`, 10, false, 'right');
    }
    addLine();
    
    // Calculate total paid correctly based on payment type
    const totalPaid = isFullPaymentOrder 
      ? (orderSubtotal + confirmationFeeAmount)  // Full payment: subtotal + fee
      : (Number(transactionData.total) || Number(transactionData.amountPaid) || confirmationFeeAmount); // Confirmation fee only
    
    addText(`Total Paid: $${Number(totalPaid).toFixed(2)}`, 12, true, 'right');
    if (isFullPaymentOrder) {
      yPosition += 3;
      addText('*** FULL AMOUNT PAID ***', 12, true, 'center');
      yPosition += 3;
      addText('No amount due at pickup', 10, false, 'center');
    } else {
      yPosition += 3;
      addText(`Amount Due at Pickup: $${orderSubtotal.toFixed(2)}`, 10, false, 'right');
    }

    // Footer - ensure it fits on the page, add new page if needed
    ensureSpaceForFooter();
    yPosition += 20;
    addLine();
    addText('Thank you for your business!', 10, true, 'center');
    addText('For support, contact us at packmycart.help@gmail.com', 8, false, 'center');

    // Generate filename and download
    const filename = `receipt_${transactionData.cartInstanceId || params.order_id}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    
    logger.debug('Receipt PDF generated and downloaded:', filename);
  };

  // Function to decrement inventory for successful payments
  const decrementInventory = async (supermarketId, cartItems) => {
    logger.debug('Starting inventory decrement for hardware store', { supermarketId, cartItems });
    
    // Decrement inventory for each product in the cart
    for (const product of cartItems) {
      try {
        // Use supermarket_id and product_id to fetch the Inventory document
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(
          inventoryRef,
          where('supermarket_id', '==', supermarketId),
          where('product_id', '==', product.productId),
          limit(1)
        );

        const snapshot = await getDocsQuery(inventoryQuery);
        
        if (!snapshot.empty) {
          const inventoryDoc = snapshot.docs[0];
          const data = inventoryDoc.data();
          
          const fields = buildInventoryDecrementFields(data, product.productTypeId, product.quantity || 0);
          if (Object.keys(fields).length > 0) {
            await updateDoc(inventoryDoc.ref, fields);
            logger.debug(`[WipayResponsePage] Updated inventory for product ${product.productId}`, fields);
          }
        } else {
          logger.warn(`[WipayResponsePage] No inventory document found for product ${product.productId} in store ${supermarketId}`);
        }
      } catch (error) {
        logger.error(`[WipayResponsePage] Error updating inventory for product ${product.productId}:`, error);
        // Don't throw the error to prevent payment processing from failing
        // Inventory updates are important but shouldn't block payment completion
      }
    }
  };

  // Function to increment inventory for failed payments (restore stock)
  // Note: Currently not used as simulated payments only decrement on success,
  // and real WiPay payments only decrement after success confirmation
  // Keeping for potential future use in edge cases
  /*
  const incrementInventory = async (supermarketId, cartItems) => {
    logger.debug('[WipayResponsePage] Starting inventory restoration for failed payment - supermarket:', supermarketId, 'with items:', cartItems);
    
    // Increment inventory for each product in the cart
    for (const product of cartItems) {
      try {
        // Use supermarket_id and product_id to fetch the Inventory document
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(
          inventoryRef,
          where('supermarket_id', '==', supermarketId),
          where('product_id', '==', product.productId),
          limit(1)
        );

        const snapshot = await getDocsQuery(inventoryQuery);
        
        if (!snapshot.empty) {
          const inventoryDoc = snapshot.docs[0];
          const data = inventoryDoc.data();
          
          const oldQty = data.quantity || 0;
          const newQty = oldQty + (product.quantity || 0);

          await updateDoc(inventoryDoc.ref, {
            quantity: newQty
          });
          logger.debug(`[WipayResponsePage] Restored inventory for product ${product.productId}: ${oldQty} -> ${newQty}`);
        } else {
          logger.warn(`[WipayResponsePage] No inventory document found for product ${product.productId} in store ${supermarketId} - cannot restore`);
        }
      } catch (error) {
        logger.error(`[WipayResponsePage] Error restoring inventory for product ${product.productId}:`, error);
        // Don't throw the error - inventory restoration is important but shouldn't block error handling
      }
    }
  };
  */

  useEffect(() => {
    const q = parseQueryParams(window.location.search);
    setParams(q);
    
    // Debug logging for WiPay response
    logger.debug('[WipayResponsePage] Full WiPay response params:', q);
    logger.debug('[WipayResponsePage] Payment status:', q.status);
    logger.debug('[WipayResponsePage] Order ID:', q.order_id);
    logger.debug('[WipayResponsePage] Transaction ID:', q.transaction_id);
    logger.debug('[WipayResponsePage] Data parameter:', q.data);
    
    const savePayment = async () => {
              try {
                const docId = q.order_id || undefined;
                
                const wipayPaymentData = {
                  ...q,
                  received_at: new Date(),
                };
                
                await setDoc(doc(db, 'WipayPayments', docId), wipayPaymentData);

            // Try to fetch the cart document by order_id
            let foundTransactionId = null;
            const cartDoc = await getDoc(doc(db, 'CartInstances', q.order_id));
            if (cartDoc.exists()) {
              foundTransactionId = cartDoc.data().transaction_id || null;
            } else {
              const cartInstancesRef = collection(db, 'CartInstances');
              const cartQuery = query(
                cartInstancesRef,
                where('order_id', '==', q.order_id),
                limit(1)
              );
              const snapshot = await getDocs(cartQuery);
              if (!snapshot.empty) {
                foundTransactionId = snapshot.docs[0].data().transaction_id || null;
              }
            }
            setCartTransactionId(foundTransactionId);

            // Check cart status before processing payment
            let currentCartStatus = null;
            if (q.order_id) {
              try {
                logger.debug('[WipayResponsePage] Checking cart status for order_id:', q.order_id);
                const cartsRef = collection(db, 'Carts');
                const cartsSnapshot = await getDocs(cartsRef);
                
                for (const cartDoc of cartsSnapshot.docs) {
                  const cartData = cartDoc.data();
                  if (cartData) {
                    for (const supermarketId in cartData) {
                      if (supermarketId === 'lastGlobalUpdate') continue;
                      
                      const supermarketCart = cartData[supermarketId];
                      if (supermarketCart && supermarketCart[q.order_id]) {
                        currentCartStatus = supermarketCart[q.order_id].status;
                        logger.debug('[WipayResponsePage] Found cart status:', currentCartStatus);
                        setCartStatus(currentCartStatus);
                        
                        // Check if cart status is invalid for payment processing
                        // Only block if payment was NOT successful AND cart has invalid status
                        if (q.status !== 'success' && StatusHelpers.isInvalidForPayment(currentCartStatus)) {
                          logger.debug('[WipayResponsePage] Cart has invalid status for failed payment:', currentCartStatus);
                          setInvalidCartStatus(true);
                          setError(`This cart has an invalid status for payment processing. Current status: ${StatusHelpers.getDisplayText(currentCartStatus)}`);
                          setSaving(false);
                          return;
                        }
                        
                        // If payment was successful and cart is awaiting_packing, that's expected
                        if (q.status === 'success' && currentCartStatus === CART_STATUS.AWAITING_PACKING) {
                          logger.debug('[WipayResponsePage] Payment successful and cart already in awaiting_packing status - this is expected after refresh');
                          // Don't block the success page, just log the status
                        }
                        
                        // If payment was successful but cart is in more advanced status, log warning but allow success page
                        if (q.status === 'success' && StatusHelpers.isMoreAdvancedThanAwaitingPacking(currentCartStatus)) {
                          logger.warn('[WipayResponsePage] Payment successful but cart is in more advanced status:', currentCartStatus, '- showing success page with warning');
                          // Don't block the success page, but this indicates a potential data integrity issue
                        }
                        break;
                      }
                    }
                    if (currentCartStatus) break;
                  }
                }
              } catch (statusError) {
                logger.error('[WipayResponsePage] Error checking cart status:', statusError);
                // Continue with payment processing if status check fails
              }
            }

                        // Create or update transaction document with all necessary fields
            if (q.status === 'success' && q.order_id) {
              const transactionRef = doc(db, 'Transactions', q.order_id);
              
                             // Check if this is a simulated payment
               if (paymentType === 'simulated') {
                 // logger.debug('Creating simulated transaction for order:', q.order_id);
                 
                 // For simulated payments, try to get the actual checkout data first
                 let checkoutData = {};
                 let cartInstanceData = null;
                 
                 // Try to parse checkout data from WiPay response
                 try {
                   if (q.data) {
                     checkoutData = JSON.parse(q.data);
                     // logger.debug('Parsed checkout data for simulated payment:', checkoutData);
                   }
                 } catch (error) {
                   logger.error('Failed to parse checkout data for simulated payment:', error);
                 }
                 
                                   // If no checkout data from WiPay, try to fetch from cart instance
                  let cartsSnapshot = null;
                  if (!checkoutData.cartItems || checkoutData.cartItems.length === 0) {
                    try {
                      // logger.debug('Attempting to fetch cart instance data for simulated payment:', q.order_id);
                      
                      // Search in Carts collection
                      const cartsRef = collection(db, 'Carts');
                      cartsSnapshot = await getDocs(cartsRef);
                      
                      for (const cartDoc of cartsSnapshot.docs) {
                        const cartData = cartDoc.data();
                        if (cartData) {
                          for (const supermarketId in cartData) {
                            if (supermarketId === 'lastGlobalUpdate') continue;
                            const supermarketCart = cartData[supermarketId];
                            if (supermarketCart && supermarketCart[q.order_id]) {
                              cartInstanceData = supermarketCart[q.order_id];
                              // logger.debug('Found cart instance data for simulated payment:', cartInstanceData);
                              break;
                            }
                          }
                          if (cartInstanceData) break;
                        }
                      }
                    } catch (error) {
                      logger.error('Failed to fetch cart instance data for simulated payment:', error);
                    }
                  }
                  
                  // Extract user ID and supermarket ID from the cart structure
                  let actualUserId = checkoutData.userId || cartInstanceData?.userId;
                  let actualSupermarketId = checkoutData.supermarketId || cartInstanceData?.supermarketId;
                  
                  // If we found cart instance data, extract the supermarket ID from the cart structure
                  if (cartInstanceData && !actualSupermarketId && cartsSnapshot) {
                    // The supermarket ID is the key in the cart structure where we found the cart instance
                    // We need to find which supermarket ID contains this cart instance
                    for (const cartDoc of cartsSnapshot.docs) {
                      const cartData = cartDoc.data();
                      if (cartData) {
                        for (const supermarketId in cartData) {
                          if (supermarketId === 'lastGlobalUpdate') continue;
                          const supermarketCart = cartData[supermarketId];
                          if (supermarketCart && supermarketCart[q.order_id]) {
                            actualSupermarketId = supermarketId;
                            actualUserId = cartDoc.id; // The cart document ID is the user ID
                            // logger.debug('Extracted actual user ID:', actualUserId, 'and supermarket ID:', actualSupermarketId);
                            break;
                          }
                        }
                        if (actualSupermarketId) break;
                      }
                    }
                  }
                  
                                     // Check existing simulated transaction status before updating
                  let existingSimulatedTransaction = null;
                  try {
                    const existingSimulatedTransactionDoc = await getDoc(transactionRef);
                    if (existingSimulatedTransactionDoc.exists()) {
                      existingSimulatedTransaction = existingSimulatedTransactionDoc.data();
                      logger.debug('[WipayResponsePage] Found existing simulated transaction with status:', existingSimulatedTransaction.status);
                    }
                  } catch (error) {
                    logger.warn('[WipayResponsePage] Error checking existing simulated transaction:', error);
                  }

                  // Determine the appropriate status for simulated transaction
                  let simulatedTransactionStatus = CART_STATUS.AWAITING_PACKING;
                  if (existingSimulatedTransaction) {
                    // If transaction already exists with a more advanced status, preserve it
                    if (StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingSimulatedTransaction.status)) {
                      simulatedTransactionStatus = existingSimulatedTransaction.status;
                      logger.debug('[WipayResponsePage] Preserving existing advanced simulated status:', simulatedTransactionStatus);
                    } else if (existingSimulatedTransaction.status === CART_STATUS.AWAITING_PACKING) {
                      simulatedTransactionStatus = CART_STATUS.AWAITING_PACKING;
                      logger.debug('[WipayResponsePage] Simulated transaction already in awaiting_packing status');
                    } else {
                      // Transaction exists but can be updated to awaiting_packing
                      simulatedTransactionStatus = CART_STATUS.AWAITING_PACKING;
                      logger.debug('[WipayResponsePage] Updating simulated transaction status from', existingSimulatedTransaction.status, 'to awaiting_packing');
                    }
                  }
                  
                                     // Fetch customer name from Users collection for simulated payment (using identity_id)
                  let simulatedCustomerName = checkoutData.customerName || cartInstanceData?.customerName || 'Customer';
                  
                  // Try to get the actual customer name from Users collection by querying identity_id
                  if (actualUserId && actualUserId !== 'simulated_user') {
                    try {
                      logger.debug('[WipayResponsePage] Fetching customer name for simulated payment identity_id:', actualUserId);
                      const usersQuery = query(
                        collection(db, 'Users'),
                        where('identity_id', '==', actualUserId),
                        limit(1)
                      );
                      const userSnapshot = await getDocs(usersQuery);
                      if (!userSnapshot.empty) {
                        const userData = userSnapshot.docs[0].data();
                        simulatedCustomerName = userData.first_name || simulatedCustomerName;
                        logger.debug('[WipayResponsePage] Fetched customer name for simulated payment:', simulatedCustomerName);
                      } else {
                        logger.warn('[WipayResponsePage] User not found for simulated payment identity_id:', actualUserId);
                      }
                    } catch (error) {
                      logger.error('[WipayResponsePage] Error fetching customer name for simulated payment:', error);
                      // Continue with fallback name
                    }
                  }
                  
                  // Create simulated transaction using actual checkout data
                  const simulatedTransactionData = {
                    userId: actualUserId || 'simulated_user',
                    supermarketId: actualSupermarketId || 'simulated_supermarket',
                    supermarketName: checkoutData.supermarketName || cartInstanceData?.supermarketName || 'Simulated hardware store',
                    customerName: simulatedCustomerName,
                    cartInstanceId: checkoutData.cartInstanceId || q.order_id,
                    cartItems: checkoutData.cartItems || cartInstanceData?.products || [
                      {
                        productId: 'simulated_product_1',
                        name: 'Simulated Product 1',
                        price: 500,
                        quantity: 2
                      },
                      {
                        productId: 'simulated_product_2', 
                        name: 'Simulated Product 2',
                        price: 300,
                        quantity: 1
                      }
                    ],
                    confirmationFee: checkoutData.confirmationFee || confirmationFee,
                    orderSubtotal: checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || 1300,
                    paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
                    deliveryOption: checkoutData.deliveryOption || cartInstanceData?.deliveryOption || 'pickup',
                    deliveryAddress: checkoutData.deliveryAddress || cartInstanceData?.deliveryAddress || null,
                    deliveryPhone: checkoutData.deliveryPhone || cartInstanceData?.deliveryPhone || null,
                    status: simulatedTransactionStatus,
                     amountPaid: parseFloat(q.total) || confirmationFee,
                     paymentGatewayTransactionId: `sim_pmt_${Date.now()}_${q.order_id}`,
                     paymentGateway: 'Simulated',
                     paymentMethod: 'Simulated Payment',
                     message: 'Simulated payment processed successfully',
                     total: parseFloat(q.total) || confirmationFee,
                     createdAt: existingSimulatedTransaction?.createdAt || serverTimestamp(),
                     updatedAt: serverTimestamp()
                   };
                 
                // Declare simulatedCartFound at the proper scope
                let simulatedCartFound = false;
                 
                                   try {
                    // Check if transaction already exists to prevent duplicates
                    const existingSimulatedTransaction = await getDoc(transactionRef);
                    if (existingSimulatedTransaction.exists()) {
                      logger.debug('[WipayResponsePage] Simulated transaction already exists, updating instead of creating new one. Cart:', simulatedTransactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                      await setDoc(transactionRef, simulatedTransactionData, { merge: true });
                    } else {
                      logger.debug('[WipayResponsePage] Creating new simulated transaction record. Cart:', simulatedTransactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                      await setDoc(transactionRef, simulatedTransactionData, { merge: true });
                    }
                    setTransactionData(simulatedTransactionData);
                    
                  // Track if notification was sent (declared at higher scope for fallback)
                  let statusWasUpdated = false;
                  let notificationSent = false;
                    
                  // Only update cart status if simulated transaction status was set to awaiting_packing
                  if (simulatedTransactionStatus === CART_STATUS.AWAITING_PACKING) {
                    logger.debug('[WipayResponsePage] Simulated transaction status is awaiting_packing, updating cart status...');
                    
                    // Find and update the cart instance in Carts collection
                    logger.debug('[WipayResponsePage] Searching for simulated cart instance with order_id:', q.order_id);
                    const cartsRef = collection(db, 'Carts');
                    const cartsSnapshot = await getDocs(cartsRef);
                    logger.debug('[WipayResponsePage] Found', cartsSnapshot.docs.length, 'cart documents to search for simulated payment');
                    for (const cartDoc of cartsSnapshot.docs) {
                      const cartData = cartDoc.data();
                      if (cartData) {
                        for (const supermarketId in cartData) {
                          if (supermarketId === 'lastGlobalUpdate') continue;
                          
                          const supermarketCart = cartData[supermarketId];
                          logger.debug('[WipayResponsePage] Checking supermarket:', supermarketId, 'for simulated cart instance with orderid:', q.order_id);
                          logger.debug('[WipayResponsePage] Available simulated cart instances:', Object.keys(supermarketCart || {}));
                          
                          // Look for cart instance with matching orderid field
                          let foundSimulatedCartInstance = null;
                          let actualSimulatedCartInstanceId = null;
                          
                          for (const cartInstanceKey of Object.keys(supermarketCart || {})) {
                            const cartInstance = supermarketCart[cartInstanceKey];
                            if (cartInstance && cartInstance.orderid === q.order_id) {
                              logger.debug('[WipayResponsePage] Found simulated cart instance with matching orderid:', cartInstanceKey);
                              foundSimulatedCartInstance = cartInstance;
                              actualSimulatedCartInstanceId = cartInstanceKey;
                              break;
                            }
                          }
                          
                          if (foundSimulatedCartInstance) {
                              logger.debug('[WipayResponsePage] Found simulated cart instance, checking current status:', foundSimulatedCartInstance.status);
                              
                              // Only update status if it's safe to do so (not more advanced than awaiting_packing)
                              statusWasUpdated = false;
                              if (StatusHelpers.canUpdateToAwaitingPacking(foundSimulatedCartInstance.status)) {
                                logger.debug('[WipayResponsePage] Updating simulated cart status from', foundSimulatedCartInstance.status, 'to awaiting_packing');
                                supermarketCart[actualSimulatedCartInstanceId].status = CART_STATUS.AWAITING_PACKING;
                            supermarketCart[actualSimulatedCartInstanceId].lastUpdatedAt = new Date();
                            
                            await setDoc(cartDoc.ref, cartData, { merge: true });
                            logger.debug('[WipayResponsePage] Cart status updated to awaiting_packing for simulated order:', actualSimulatedCartInstanceId);
                                statusWasUpdated = true;
                            } else if (foundSimulatedCartInstance.status === CART_STATUS.AWAITING_PACKING) {
                              logger.debug('[WipayResponsePage] Simulated cart already in awaiting_packing status - checking if notification needed');
                              
                              // Check if this is a fresh payment (not a page refresh)
                              // If the cart was just updated (within last 30 seconds), send notification
                              const lastUpdatedAt = foundSimulatedCartInstance.lastUpdatedAt;
                              const now = new Date();
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let lastUpdatedDate = lastUpdatedAt;
                              if (lastUpdatedAt && typeof lastUpdatedAt.toDate === 'function') {
                                lastUpdatedDate = lastUpdatedAt.toDate();
                              } else if (!(lastUpdatedAt instanceof Date)) {
                                lastUpdatedDate = null;
                              }
                              const timeDiff = lastUpdatedDate ? (now - lastUpdatedDate) / 1000 : 999; // seconds
                              
                              // Also check if this is a fresh transaction (created within last 60 seconds)
                              const transactionCreatedAt = simulatedTransactionData.createdAt;
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let transactionCreatedDate = transactionCreatedAt;
                              if (transactionCreatedAt && typeof transactionCreatedAt.toDate === 'function') {
                                transactionCreatedDate = transactionCreatedAt.toDate();
                              } else if (!(transactionCreatedAt instanceof Date)) {
                                transactionCreatedDate = null;
                              }
                              const transactionTimeDiff = transactionCreatedDate ? (now - transactionCreatedDate) / 1000 : 999;
                              
                              if (timeDiff < 30 || transactionTimeDiff < 60) {
                                logger.debug('[WipayResponsePage] Fresh payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago), sending notification');
                                statusWasUpdated = true; // This will trigger notification
                              } else {
                                logger.debug('[WipayResponsePage] Old payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago) - likely a page refresh, no notification needed');
                              }
                            } else {
                                logger.warn('[WipayResponsePage] Simulated cart is in more advanced status:', foundSimulatedCartInstance.status, '- cannot update to awaiting_packing');
                              }
                              
                              // Notify employees ONLY if status was actually updated to awaiting_packing
                              if (statusWasUpdated) {
                                if (enableEmployeeNotifications) {
                                  logger.debug('[WipayResponsePage] Notifying employees about new simulated order awaiting packing');
                                  
                                  // Fetch user's actual name instead of using userId
                                  let customerName = 'Customer';
                                  try {
                                    const userQuery = query(collection(db, 'Users'), where('identity_id', '==', simulatedTransactionData.userId));
                                    const userSnapshot = await getDocs(userQuery);
                                    if (!userSnapshot.empty) {
                                      const userData = userSnapshot.docs[0].data();
                                      customerName = userData.first_name || userData.displayName || 'Customer';
                                    }
                                  } catch (userError) {
                                    logger.warn('[WipayResponsePage] Failed to fetch user name for notification:', userError);
                                  }
                                  
                                  // Don't block on notification - fire and forget to prevent UI delays
                                  fetch(EMPLOYEE_NOTIFICATION_FUNCTION_URL, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                      orderId: actualSimulatedCartInstanceId,
                                      transactionId: q.order_id, // Use the actual transaction document ID for proper routing
                                      customerName: customerName,
                                      supermarketId: simulatedTransactionData.supermarketId
                                    })
                                  })
                                  .then(response => {
                                    if (!response.ok) {
                                      logger.warn('[WipayResponsePage] Employee notification failed for simulated order:', response.status);
                                    } else {
                                      return response.json();
                                    }
                                  })
                                  .then(result => {
                                    if (result && result.success) {
                                      logger.debug('[WipayResponsePage] Employee notification sent successfully for simulated order');
                                    } else if (result) {
                                      logger.warn('[WipayResponsePage] Employee notification returned success: false for simulated order', result);
                                    }
                                  })
                                  .catch(notificationError => {
                                    logger.warn('[WipayResponsePage] Failed to notify employees for simulated order:', notificationError);
                                    // Don't fail the order process if notification fails
                                  });
                                  // Continue immediately without waiting for notification
                                  notificationSent = true;
                                } else {
                                  logger.debug('[WipayResponsePage] Employee notifications are disabled via feature flag');
                                }
                              } else {
                                logger.debug('[WipayResponsePage] No employee notification sent - cart status was not updated to awaiting_packing');
                              }
                            
                            simulatedCartFound = true;
                            break;
                          }
                        }
                        if (simulatedCartFound) break;
                      }
                      }
                    } else {
                      logger.debug('[WipayResponsePage] Simulated transaction status is', simulatedTransactionStatus, '- skipping cart status update');
                    }
                    
                    // Send notification if transaction was created successfully, even if cart wasn't found or notification wasn't sent
                    if (simulatedTransactionStatus === CART_STATUS.AWAITING_PACKING && enableEmployeeNotifications && !notificationSent) {
                      logger.debug('[WipayResponsePage] Sending fallback employee notification for simulated payment (cart found:', simulatedCartFound, ', status updated:', statusWasUpdated, ')');
                      
                      // Fetch user's actual name instead of using userId
                      let customerName = simulatedTransactionData.customerName || 'Customer';
                      try {
                        const userQuery = query(collection(db, 'Users'), where('identity_id', '==', simulatedTransactionData.userId));
                        const userSnapshot = await getDocs(userQuery);
                        if (!userSnapshot.empty) {
                          const userData = userSnapshot.docs[0].data();
                          customerName = userData.first_name || userData.displayName || customerName;
                        }
                      } catch (userError) {
                        logger.warn('[WipayResponsePage] Failed to fetch user name for fallback notification:', userError);
                      }
                      
                      // Use cartInstanceId from transaction data, or fallback to transaction ID
                      const orderId = simulatedTransactionData.cartInstanceId || q.order_id;
                      const transactionId = q.order_id;
                      
                      // Don't block on notification - fire and forget to prevent UI delays
                      fetch(EMPLOYEE_NOTIFICATION_FUNCTION_URL, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          orderId: orderId,
                          transactionId: transactionId,
                          customerName: customerName,
                          supermarketId: simulatedTransactionData.supermarketId
                        })
                      })
                      .then(response => {
                        if (!response.ok) {
                          logger.warn('[WipayResponsePage] Fallback employee notification failed for simulated order:', response.status);
                        } else {
                          return response.json();
                        }
                      })
                      .then(result => {
                        if (result && result.success) {
                          logger.debug('[WipayResponsePage] Fallback employee notification sent successfully for simulated order');
                        } else if (result) {
                          logger.warn('[WipayResponsePage] Fallback employee notification returned success: false for simulated order', result);
                        }
                      })
                      .catch(notificationError => {
                        logger.warn('[WipayResponsePage] Failed to send fallback notification to employees for simulated order:', notificationError);
                        // Don't fail the order process if notification fails
                      });
                    }
                    
                    if (!simulatedCartFound) {
                      logger.warn('[WipayResponsePage] Simulated cart instance not found for order_id:', q.order_id);
                    }
                  } catch (error) {
                    logger.error('[WipayResponsePage] Error saving simulated transaction:', error);
                    throw error;
                  }
              } else {
                                // For real WiPay payments, use the existing logic
                // Parse the data parameter to get the original checkout data
                let checkoutData = {};
                try {
                  if (q.data) {
                    // Handle URL-encoded JSON data from WiPay
                    let dataString = q.data;
                    logger.debug('[WipayResponsePage] Raw data string:', dataString);
                    
                    try {
                      // First, try to decode URL encoding
                      dataString = decodeURIComponent(dataString);
                      logger.debug('[WipayResponsePage] After URL decode:', dataString);
                      
                      // Check if the data is wrapped in quotes (JSON string containing JSON)
                      if (dataString.startsWith('"') && dataString.endsWith('"')) {
                        logger.debug('[WipayResponsePage] Data is wrapped in quotes, removing them');
                        dataString = dataString.slice(1, -1);
                        logger.debug('[WipayResponsePage] After removing outer quotes:', dataString);
                      }
                      
                      // Handle JSON string escaping
                      dataString = dataString.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
                      logger.debug('[WipayResponsePage] After escape replacement:', dataString);
                      
                      checkoutData = JSON.parse(dataString);
                      logger.debug('[WipayResponsePage] Successfully parsed checkout data:', checkoutData);
                      logger.debug('[WipayResponsePage] Cart items from checkout data:', checkoutData.cartItems);
                    } catch (parseError) {
                      logger.error('[WipayResponsePage] Failed to parse data parameter:', parseError);
                      logger.error('[WipayResponsePage] Data string that failed:', dataString);
                      checkoutData = {};
                    }
                  }
                } catch (error) {
                  logger.error('[WipayResponsePage] Failed to parse checkout data:', error);
                  logger.error('[WipayResponsePage] Raw data parameter:', q.data);
                }
               
                // If we don't have checkout data from WiPay, try to fetch it from the cart instance
                let cartInstanceData = null;
                if (!checkoutData.cartItems || checkoutData.cartItems.length === 0) {
                  try {
                    // logger.debug('Attempting to fetch cart instance data for:', q.order_id);
                    
                    // First, try to find the cart instance in the Carts collection
                    const cartsRef = collection(db, 'Carts');
                    const cartsSnapshot = await getDocs(cartsRef);
                    // logger.debug('Searching through', cartsSnapshot.docs.length, 'cart documents');
                    
                    for (const cartDoc of cartsSnapshot.docs) {
                      const cartData = cartDoc.data();
                      // logger.debug('Checking cart for user:', cartDoc.id);
                      
                      if (cartData) {
                        // Search through all supermarkets in this user's cart
                        for (const supermarketId in cartData) {
                          if (supermarketId === 'lastGlobalUpdate') continue;
                          
                          const supermarketCart = cartData[supermarketId];
                          // logger.debug('Checking supermarket:', supermarketId, 'with instances:', Object.keys(supermarketCart || {}));
                          
                          if (supermarketCart && supermarketCart[q.order_id]) {
                            cartInstanceData = supermarketCart[q.order_id];
                            // logger.debug('Found cart instance data in Carts collection:', cartInstanceData);
                            break;
                          }
                        }
                        if (cartInstanceData) break;
                      }
                    }
                    
                    // If still not found, try the Users collection as fallback
                    if (!cartInstanceData) {
                      // logger.debug('Not found in Carts collection, trying Users collection...');
                      const usersRef = collection(db, 'Users');
                      const usersSnapshot = await getDocs(usersRef);
                      
                      for (const userDoc of usersSnapshot.docs) {
                        const userData = userDoc.data();
                        if (userData && userData.cart) {
                          // Search through all supermarkets in the user's cart
                          for (const supermarketId in userData.cart) {
                            if (supermarketId === 'lastGlobalUpdate') continue;
                            const supermarketCart = userData.cart[supermarketId];
                            if (supermarketCart && supermarketCart[q.order_id]) {
                              cartInstanceData = supermarketCart[q.order_id];
                              // logger.debug('Found cart instance data in Users collection:', cartInstanceData);
                              break;
                            }
                          }
                          if (cartInstanceData) break;
                        }
                      }
                    }
                    
                    if (!cartInstanceData) {
                      // logger.debug('Cart instance data not found in any collection');
                    }
                  } catch (error) {
                    logger.error('Failed to fetch cart instance data:', error);
                  }
                }
               
                // Check existing transaction status before updating
                let existingTransaction = null;
                try {
                  const existingTransactionDoc = await getDoc(transactionRef);
                  if (existingTransactionDoc.exists()) {
                    existingTransaction = existingTransactionDoc.data();
                    logger.debug('[WipayResponsePage] Found existing transaction with status:', existingTransaction.status);
                  }
                } catch (error) {
                  logger.warn('[WipayResponsePage] Error checking existing transaction:', error);
                }

                // Determine the appropriate status based on existing transaction
                let transactionStatus = CART_STATUS.AWAITING_PACKING;
                if (existingTransaction) {
                  // If transaction already exists with a more advanced status, preserve it
                  if (StatusHelpers.isMoreAdvancedThanAwaitingPacking(existingTransaction.status)) {
                    transactionStatus = existingTransaction.status;
                    logger.debug('[WipayResponsePage] Preserving existing advanced status:', transactionStatus);
                  } else if (existingTransaction.status === CART_STATUS.AWAITING_PACKING) {
                    transactionStatus = CART_STATUS.AWAITING_PACKING;
                    logger.debug('[WipayResponsePage] Transaction already in awaiting_packing status');
                  } else {
                    // Transaction exists but can be updated to awaiting_packing
                    transactionStatus = CART_STATUS.AWAITING_PACKING;
                    logger.debug('[WipayResponsePage] Updating transaction status from', existingTransaction.status, 'to awaiting_packing');
                  }
                }
               
                // Fetch customer name from Users collection using userId (which is identity_id)
                const userId = checkoutData.userId || cartInstanceData?.userId || q.user_id || 'unknown';
                let customerName = checkoutData.customerName || cartInstanceData?.customerName || q.customer_name || 'Customer';
                
                // Try to get the actual customer name from Users collection by querying identity_id
                if (userId && userId !== 'unknown') {
                  try {
                    logger.debug('[WipayResponsePage] Fetching customer name for identity_id:', userId);
                    const usersQuery = query(
                      collection(db, 'Users'),
                      where('identity_id', '==', userId),
                      limit(1)
                    );
                    const userSnapshot = await getDocs(usersQuery);
                    if (!userSnapshot.empty) {
                      const userData = userSnapshot.docs[0].data();
                      customerName = userData.first_name || customerName;
                      logger.debug('[WipayResponsePage] Fetched customer name:', customerName);
                    } else {
                      logger.warn('[WipayResponsePage] User not found for identity_id:', userId);
                    }
                  } catch (error) {
                    logger.error('[WipayResponsePage] Error fetching customer name:', error);
                    // Continue with fallback name
                  }
                }
                
                const transactionData = {
                  userId: userId,
                  supermarketId: checkoutData.supermarketId || cartInstanceData?.supermarketId || q.supermarket_id || 'unknown',
                  supermarketName: checkoutData.supermarketName || cartInstanceData?.supermarketName || q.supermarket_name || 'Unknown hardware store',
                  customerName: customerName,
                  cartInstanceId: checkoutData.cartInstanceId || q.order_id,
                  cartItems: checkoutData.cartItems || cartInstanceData?.products || (q.cart_items ? JSON.parse(q.cart_items) : []),
                  confirmationFee: checkoutData.confirmationFee || confirmationFee,
                  orderSubtotal: checkoutData.orderSubtotal || cartInstanceData?.orderSubtotal || parseFloat(q.order_subtotal) || 0,
                  paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
                  deliveryOption: checkoutData.deliveryOption || cartInstanceData?.deliveryOption || q.delivery_option || 'pickup',
                  deliveryAddress: checkoutData.deliveryAddress || cartInstanceData?.deliveryAddress || q.delivery_address || null,
                  deliveryPhone: checkoutData.deliveryPhone || cartInstanceData?.deliveryPhone || q.delivery_phone || null,
                  status: transactionStatus,
                  amountPaid: parseFloat(q.total) || (checkoutData.paymentType === 'full' ? ((checkoutData.orderSubtotal || 0) + (checkoutData.confirmationFee || confirmationFee)) : (checkoutData.confirmationFee || confirmationFee)),
                  paymentGatewayTransactionId: q.transaction_id || `wipay_${Date.now()}`,
                  paymentGateway: paymentGateway,
                  paymentMethod: q.method || paymentMethod,
                  message: q.message || 'Payment processed successfully',
                  total: parseFloat(q.total) || (checkoutData.paymentType === 'full' ? ((checkoutData.orderSubtotal || 0) + (checkoutData.confirmationFee || confirmationFee)) : (checkoutData.confirmationFee || confirmationFee)),
                  createdAt: existingTransaction?.createdAt || serverTimestamp(),
                  updatedAt: serverTimestamp()
                };
                
                // Declare cartFound at the proper scope
                let cartFound = false;
                
                try {
                  // Check if transaction already exists to prevent duplicates
                  const existingTransaction = await getDoc(transactionRef);
                  if (existingTransaction.exists()) {
                    logger.debug('[WipayResponsePage] Real WiPay transaction already exists, updating instead of creating new one. Cart:', transactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                    await setDoc(transactionRef, transactionData, { merge: true });
                  } else {
                    logger.debug('[WipayResponsePage] Creating new real WiPay transaction record. Cart:', transactionData.cartInstanceId, 'WiPay ID:', q.order_id);
                    await setDoc(transactionRef, transactionData, { merge: true });
                  }
                  setTransactionData(transactionData);
                  
                  // Decrement inventory for successful real WiPay payments
                  if (checkoutData.supermarketId && checkoutData.cartItems) {
                    logger.debug('[WipayResponsePage] Decrementing inventory for successful real WiPay payment');
                    await decrementInventory(checkoutData.supermarketId, checkoutData.cartItems);
                  } else {
                    logger.warn('[WipayResponsePage] Cannot decrement inventory: missing supermarketId or cartItems in checkoutData');
                  }
                  
                  // Only update cart status if transaction status was set to awaiting_packing
                  if (transactionStatus === CART_STATUS.AWAITING_PACKING) {
                    logger.debug('[WipayResponsePage] Transaction status is awaiting_packing, updating cart status...');
                  
                  // Find and update the cart instance in Carts collection
                  logger.debug('[WipayResponsePage] Searching for cart instance with order_id:', q.order_id);
                  const cartsRef = collection(db, 'Carts');
                  const cartsSnapshot = await getDocs(cartsRef);
                  logger.debug('[WipayResponsePage] Found', cartsSnapshot.docs.length, 'cart documents to search');
                  for (const cartDoc of cartsSnapshot.docs) {
                    const cartData = cartDoc.data();
                    if (cartData) {
                      for (const supermarketId in cartData) {
                        if (supermarketId === 'lastGlobalUpdate') continue;
                        
                        const supermarketCart = cartData[supermarketId];
                        logger.debug('[WipayResponsePage] Checking supermarket:', supermarketId, 'for cart instance with orderid:', q.order_id);
                        logger.debug('[WipayResponsePage] Available cart instances:', Object.keys(supermarketCart || {}));
                        
                        // Look for cart instance with matching orderid field
                        let foundCartInstance = null;
                        let actualCartInstanceId = null;
                        
                        for (const cartInstanceKey of Object.keys(supermarketCart || {})) {
                          const cartInstance = supermarketCart[cartInstanceKey];
                          if (cartInstance && cartInstance.orderid === q.order_id) {
                            logger.debug('[WipayResponsePage] Found cart instance with matching orderid:', cartInstanceKey);
                            foundCartInstance = cartInstance;
                            actualCartInstanceId = cartInstanceKey;
                            break;
                          }
                        }
                        
                        if (foundCartInstance) {
                            logger.debug('[WipayResponsePage] Found cart instance, checking current status:', foundCartInstance.status);
                            
                            // Only update status if it's safe to do so (not more advanced than awaiting_packing)
                            let statusWasUpdated = false;
                            if (StatusHelpers.canUpdateToAwaitingPacking(foundCartInstance.status)) {
                              logger.debug('[WipayResponsePage] Updating status from', foundCartInstance.status, 'to awaiting_packing');
                              supermarketCart[actualCartInstanceId].status = CART_STATUS.AWAITING_PACKING;
                          supermarketCart[actualCartInstanceId].lastUpdatedAt = new Date();
                          
                          await setDoc(cartDoc.ref, cartData, { merge: true });
                          logger.debug('[WipayResponsePage] Cart status updated to awaiting_packing for order:', actualCartInstanceId);
                              statusWasUpdated = true;
                            } else if (foundCartInstance.status === CART_STATUS.AWAITING_PACKING) {
                              logger.debug('[WipayResponsePage] Cart already in awaiting_packing status - checking if notification needed');
                              
                              // Check if this is a fresh payment (not a page refresh)
                              // If the cart was just updated (within last 30 seconds), send notification
                              const lastUpdatedAt = foundCartInstance.lastUpdatedAt;
                              const now = new Date();
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let lastUpdatedDate = lastUpdatedAt;
                              if (lastUpdatedAt && typeof lastUpdatedAt.toDate === 'function') {
                                lastUpdatedDate = lastUpdatedAt.toDate();
                              } else if (!(lastUpdatedAt instanceof Date)) {
                                lastUpdatedDate = null;
                              }
                              const timeDiff = lastUpdatedDate ? (now - lastUpdatedDate) / 1000 : 999; // seconds
                              
                              // Also check if this is a fresh transaction (created within last 60 seconds)
                              const transactionCreatedAt = transactionData.createdAt;
                              
                              // Handle both Firestore Timestamp and JavaScript Date objects
                              let transactionCreatedDate = transactionCreatedAt;
                              if (transactionCreatedAt && typeof transactionCreatedAt.toDate === 'function') {
                                transactionCreatedDate = transactionCreatedAt.toDate();
                              } else if (!(transactionCreatedAt instanceof Date)) {
                                transactionCreatedDate = null;
                              }
                              const transactionTimeDiff = transactionCreatedDate ? (now - transactionCreatedDate) / 1000 : 999;
                              
                              if (timeDiff < 30 || transactionTimeDiff < 60) {
                                logger.debug('[WipayResponsePage] Fresh payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago), sending notification');
                                statusWasUpdated = true; // This will trigger notification
                              } else {
                                logger.debug('[WipayResponsePage] Old payment detected (cart updated', Math.round(timeDiff), 's ago, transaction created', Math.round(transactionTimeDiff), 's ago) - likely a page refresh, no notification needed');
                              }
                            } else {
                              logger.warn('[WipayResponsePage] Cart is in more advanced status:', foundCartInstance.status, '- cannot update to awaiting_packing');
                            }
                            
                            // Notify employees ONLY if status was actually updated to awaiting_packing
                            logger.debug('[WipayResponsePage] Notification check:', {
                              statusWasUpdated,
                              enableEmployeeNotifications,
                              hasSupermarketId: !!transactionData.supermarketId,
                              supermarketId: transactionData.supermarketId,
                              orderId: actualCartInstanceId,
                              transactionId: docId
                            });
                            
                            if (statusWasUpdated) {
                              if (enableEmployeeNotifications) {
                                logger.debug('[WipayResponsePage] Notifying employees about new order awaiting packing');
                                logger.info('[WipayResponsePage] Calling employee notification function:', EMPLOYEE_NOTIFICATION_FUNCTION_URL);
                                
                                // Fetch user's actual name instead of using userId
                                let customerName = 'Customer';
                                try {
                                  const userQuery = query(collection(db, 'Users'), where('identity_id', '==', transactionData.userId));
                                  const userSnapshot = await getDocs(userQuery);
                                  if (!userSnapshot.empty) {
                                    const userData = userSnapshot.docs[0].data();
                                    customerName = userData.first_name || userData.displayName || 'Customer';
                                  }
                                } catch (userError) {
                                  logger.warn('[WipayResponsePage] Failed to fetch user name for notification:', userError);
                                }
                                
                                const notificationPayload = {
                                  orderId: actualCartInstanceId,
                                  transactionId: docId, // Use the actual transaction document ID for proper routing
                                  customerName: customerName,
                                  supermarketId: transactionData.supermarketId
                                };
                                
                                logger.info('[WipayResponsePage] Sending employee notification request:', {
                                  url: EMPLOYEE_NOTIFICATION_FUNCTION_URL,
                                  payload: notificationPayload
                                });
                                
                                // Don't block on notification - fire and forget to prevent UI delays
                                fetch(EMPLOYEE_NOTIFICATION_FUNCTION_URL, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify(notificationPayload)
                                })
                                .then(response => {
                                  logger.info('[WipayResponsePage] Employee notification response status:', response.status, response.statusText);
                                  if (!response.ok) {
                                    logger.warn('[WipayResponsePage] Employee notification failed:', response.status);
                                    logger.error('[WipayResponsePage] Employee notification HTTP error:', response.status, response.statusText);
                                  } else {
                                    return response.json();
                                  }
                                })
                                .then(result => {
                                  logger.info('[WipayResponsePage] Employee notification result:', result);
                                  if (result && result.success) {
                                    logger.debug('[WipayResponsePage] Employee notification sent successfully');
                                    logger.info('[WipayResponsePage] ✅ Employee notification sent successfully:', result);
                                  } else if (result) {
                                    logger.warn('[WipayResponsePage] Employee notification returned success: false', result);
                                    logger.warn('[WipayResponsePage] ⚠️ Employee notification returned success: false', result);
                                  }
                                })
                                .catch(notificationError => {
                                  logger.warn('[WipayResponsePage] Failed to notify employees:', notificationError);
                                  logger.error('[WipayResponsePage] ❌ Failed to notify employees:', notificationError);
                                  // Don't fail the order process if notification fails
                                });
                                // Continue immediately without waiting for notification
                              } else {
                                logger.debug('[WipayResponsePage] Employee notifications are disabled via feature flag');
                                logger.info('[WipayResponsePage] ⏭️ Employee notifications are disabled via feature flag');
                              }
                            } else {
                              logger.debug('[WipayResponsePage] No employee notification sent - cart status was not updated to awaiting_packing');
                              logger.info('[WipayResponsePage] ⏭️ No employee notification sent - cart status was not updated to awaiting_packing');
                            }
                          
                          cartFound = true;
                          break;
                        }
                      }
                      if (cartFound) break;
                    }
                    }
                  } else {
                    logger.debug('[WipayResponsePage] Transaction status is', transactionStatus, '- skipping cart status update');
                  }
                  
                  if (!cartFound) {
                    logger.warn('[WipayResponsePage] Cart instance not found for order_id:', q.order_id);
                  }
                } catch (error) {
                  logger.error('[WipayResponsePage] Error saving real WiPay transaction:', error);
                  throw error;
                }
              }
            } else if (q.status !== 'success') {
              // Payment failed - show failure message, don't restore cart
              logger.debug('[WipayResponsePage] Payment failed - no cart restoration needed');
              logger.debug('[WipayResponsePage] Order ID:', q.order_id, 'Status:', q.status);
              logger.debug('[WipayResponsePage] Full payment params:', q);
              
              // For failed payments, extract transaction data for navigation purposes
              let failedTransactionData = null;
              
              // Try to parse checkout data from the data parameter for failed payments
              if (q.data) {
                try {
                  let dataString = q.data;
                  // Handle URL-encoded JSON data
                  dataString = decodeURIComponent(dataString);
                  
                  // Check if the data is wrapped in quotes (JSON string containing JSON)
                  if (dataString.startsWith('"') && dataString.endsWith('"')) {
                    dataString = dataString.slice(1, -1);
                  }
                  
                  // Handle JSON string escaping
                  dataString = dataString.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
                  
                  const checkoutData = JSON.parse(dataString);
                  logger.debug('[WipayResponsePage] Parsed checkout data for failed payment:', checkoutData);
                  
                  failedTransactionData = {
                    supermarketId: checkoutData.supermarketId || q.supermarket_id || 'unknown',
                    cartInstanceId: checkoutData.cartInstanceId || q.order_id,
                    userId: checkoutData.userId || q.user_id || 'unknown',
                    supermarketName: checkoutData.supermarketName || q.supermarket_name || 'Unknown hardware store',
                    customerName: checkoutData.customerName || q.customer_name || 'Unknown Customer',
                    cartItems: checkoutData.cartItems || [],
                    confirmationFee: checkoutData.confirmationFee || confirmationFee,
                    orderSubtotal: checkoutData.orderSubtotal || parseFloat(q.order_subtotal) || 0,
                    paymentType: checkoutData.paymentType || q.payment_type || 'confirmation_fee_only',
                    deliveryOption: checkoutData.deliveryOption || q.delivery_option || 'pickup',
                    deliveryAddress: checkoutData.deliveryAddress || q.delivery_address || null,
                    deliveryPhone: checkoutData.deliveryPhone || q.delivery_phone || null,
                    status: CART_STATUS.PAYMENT_FAILED,
                    amountPaid: 0,
                    paymentGatewayTransactionId: q.transaction_id || `failed_${Date.now()}`,
                    paymentGateway: paymentGateway,
                    paymentMethod: q.method || paymentMethod,
                    message: q.message || 'Payment failed',
                    total: parseFloat(q.total) || confirmationFee,
                    createdAt: new Date(),
                    updatedAt: new Date()
                  };
                } catch (parseError) {
                  logger.error('[WipayResponsePage] Failed to parse checkout data for failed payment:', parseError);
                  // Create minimal transaction data with available information
                  failedTransactionData = {
                    supermarketId: q.supermarket_id || 'unknown',
                    cartInstanceId: q.order_id || 'unknown',
                    userId: q.user_id || 'unknown',
                    supermarketName: q.supermarket_name || 'Unknown hardware store',
                    customerName: q.customer_name || 'Unknown Customer',
                    cartItems: q.cart_items ? JSON.parse(q.cart_items) : [],
                    confirmationFee: parseFloat(q.total) || confirmationFee,
                    orderSubtotal: parseFloat(q.order_subtotal) || 0,
                    deliveryOption: q.delivery_option || 'pickup',
                    deliveryAddress: q.delivery_address || null,
                    deliveryPhone: q.delivery_phone || null,
                    status: CART_STATUS.PAYMENT_FAILED,
                    amountPaid: 0,
                    paymentGatewayTransactionId: q.transaction_id || `failed_${Date.now()}`,
                    paymentGateway: paymentGateway,
                    paymentMethod: q.method || paymentMethod,
                    message: q.message || 'Payment failed',
                    total: parseFloat(q.total) || confirmationFee,
                    createdAt: new Date(),
                    updatedAt: new Date()
                  };
                }
              }
              
              // Set transaction data for failed payments to enable proper navigation
              if (failedTransactionData) {
                setTransactionData(failedTransactionData);
                logger.debug('[WipayResponsePage] Set transaction data for failed payment:', failedTransactionData);
              }
              
              // For failed payments, just log the failure and show the error page
              // Note: No inventory restoration needed as simulated payments only decrement on success
              logger.debug('[WipayResponsePage] Payment failed - no cart restoration needed for failed payments');
              setCartRestored(false);
            }

            setSaving(false);
          } catch (err) {
            setError(`Failed to save payment details: ${  err.message}`);
            setSaving(false);
          }
        };
    savePayment();
    // eslint-disable-next-line
  }, []);

  const isSuccess = params.status === 'success';
  
  // Helper function to determine if this is a full payment order
  // Checks paymentType field first, then falls back to comparing amounts
  const isFullPayment = useCallback(() => {
    if (!transactionData) return false;
    
    // Check paymentType field first
    if (transactionData.paymentType === 'full' || params.payment_type === 'full') {
      return true;
    }
    
    // Fallback: Check if amount paid equals order subtotal + confirmation fee
    const orderSubtotal = transactionData.orderSubtotal || 0;
    const confirmationFeeAmount = transactionData.confirmationFee || confirmationFee;
    const expectedFullTotal = orderSubtotal + confirmationFeeAmount;
    const amountPaid = parseFloat(transactionData.amountPaid) || parseFloat(transactionData.total) || parseFloat(params.total) || 0;
    
    // If amount paid is close to or equals the full total (within 0.01 for rounding), it's a full payment
    if (orderSubtotal > 0 && Math.abs(amountPaid - expectedFullTotal) < 0.01) {
      return true;
    }
    
    return false;
  }, [transactionData, params.payment_type, params.total, confirmationFee]);
  
  // Debug logging for payment status
  useEffect(() => {
    if (params.status) {
      logger.debug('[WipayResponsePage] Payment status detected:', params.status);
      logger.debug('[WipayResponsePage] isSuccess calculated as:', isSuccess);
      logger.debug('[WipayResponsePage] cartRestored state:', cartRestored);
      if (transactionData) {
        logger.debug('[WipayResponsePage] transactionData.paymentType:', transactionData.paymentType);
        logger.debug('[WipayResponsePage] params.payment_type:', params.payment_type);
        logger.debug('[WipayResponsePage] isFullPayment():', isFullPayment());
        logger.debug('[WipayResponsePage] amountPaid:', transactionData.amountPaid || transactionData.total || params.total);
        logger.debug('[WipayResponsePage] orderSubtotal:', transactionData.orderSubtotal);
        logger.debug('[WipayResponsePage] confirmationFee:', transactionData.confirmationFee);
      }
    }
  }, [params.status, params.payment_type, params.total, isSuccess, cartRestored, transactionData, isFullPayment]);

  return (
    <Layout>
      <style>
        {`
          .payment-response-card {
            border: none;
            border-radius: 12px;
          }
          .payment-response-card .btn {
            border-radius: 8px;
            font-weight: 500;
            transition: all 0.2s ease-in-out;
          }
          .payment-response-card .btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }
          .payment-response-card .btn-primary {
            background: linear-gradient(135deg, #f38118, #d66f14);
            border: none;
          }
          .payment-response-card .btn-success {
            background: linear-gradient(135deg, #28a745, #1e7e34);
            border: none;
          }
          .payment-response-card .btn-warning {
            background: linear-gradient(135deg, #ffc107, #e0a800);
            border: none;
            color: #212529;
          }
          .payment-response-card .btn-outline-secondary {
            border: 2px solid #6c757d;
            color: #6c757d;
          }
          .payment-response-card .btn-outline-secondary:hover {
            background-color: #6c757d;
            border-color: #6c757d;
            color: white;
          }
        `}
      </style>
      <Container className="mt-3 mt-md-5 px-3">
        <Card className="mx-auto shadow-sm payment-response-card" style={{maxWidth: 600}}>
          <Card.Body>
            <Card.Title className={`text-center mb-4 ${isSuccess ? "text-success" : "text-danger"}`}>
              <i className={`fas ${isSuccess ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
              {isSuccess ? "Payment Successful" : "Payment Failed"}
            </Card.Title>
            {saving && <Spinner animation="border" className="my-3" />}
            {error && (
              <Alert variant={invalidCartStatus ? "warning" : "danger"}>
                {invalidCartStatus ? (
                  <>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Invalid Cart Status</strong>
                    <br />
                    {error}
                    <br />
                    <small className="text-muted">Order ID: {params.order_id}</small>
                  </>
                ) : (
                  error
                )}
              </Alert>
            )}
            {!saving && !error && (
              <div>
                {/* Payment Failed Notice */}
                {!isSuccess && (
                  <Alert variant={cartRestored ? "warning" : "danger"} className="mb-3">
                    <i className={`fas ${cartRestored ? 'fa-exclamation-triangle' : 'fa-times-circle'} me-2`}></i>
                    <strong>Payment Failed</strong>
                    <br />
                    {cartRestored ? (
                      <>
                        Your cart has been restored to active status. You can:
                        <ul className="mb-0 mt-2">
                          <li>Review and modify your order</li>
                          <li>Try the payment again</li>
                          <li>Contact support if the issue persists</li>
                        </ul>
                      </>
                    ) : (
                      <>
                        <small className="text-muted">Order ID: {params.order_id}</small>
                      </>
                    )}
                  </Alert>
                )}
                
                {/* Full Payment Notice for successful payments */}
                {isSuccess && isFullPayment() && (
                  <Alert variant="success" className="mb-3 text-center">
                    <strong><i className="fas fa-check-circle me-2"></i>FULL PAYMENT ORDER</strong>
                    <br />
                    <small>The full order total has been paid. Confirmation fee collected as processing fee.</small>
                  </Alert>
                )}
                
                <div className="row text-start">
                  <div className="col-md-6">
                    <p className="mb-2"><strong>Customer Name:</strong> <span className="text-break">{transactionData?.customerName || 'Customer'}</span></p>
                    <p className="mb-2"><strong>Order ID:</strong> <span className="text-break font-monospace">{transactionData?.cartInstanceId || params.order_id}</span></p>
                    <p className="mb-2"><strong>Hardware store:</strong> <span className="text-break">{transactionData?.supermarketName || 'Unknown hardware store'}</span></p>
                    <p className="mb-2"><strong>Status:</strong> <span className={`badge ${invalidCartStatus ? 'bg-warning' : isSuccess ? 'bg-success' : 'bg-danger'}`}>{invalidCartStatus ? StatusHelpers.getDisplayText(cartStatus) : (transactionData?.status || params.status)}</span></p>
                    <p className="mb-2"><strong>Payment Type:</strong> {isFullPayment() ? (
                      <span className="badge bg-success"><i className="fas fa-check-circle me-1"></i>Full Payment</span>
                    ) : (
                      <span className="badge bg-secondary">Confirmation Fee Only</span>
                    )}</p>
                    <p className="mb-2"><strong>Message:</strong> <span className="text-break">{transactionData?.message || params.message || 'Payment processed'}</span></p>
                    <p className="mb-2"><strong>Transaction ID:</strong> <span className="text-break font-monospace">{transactionData?.paymentGatewayTransactionId || params.transaction_id || 'N/A'}</span></p>
                    {isFullPayment() ? (
                      <>
                        <p className="mb-2"><strong>Order Subtotal:</strong> <span className="fw-bold">${(Number(transactionData?.orderSubtotal) || 0).toFixed(2)}</span> <span className="text-success">(Paid)</span></p>
                        <p className="mb-2"><strong>Processing Fee (Confirmation Fee):</strong> <span className="fw-bold">${(Number(transactionData?.confirmationFee) || confirmationFee).toFixed(2)}</span> <span className="text-muted">(Included)</span></p>
                        <p className="mb-2"><strong>Total Paid:</strong> <span className="fw-bold fs-5 text-success">${((Number(transactionData?.orderSubtotal) || 0) + (Number(transactionData?.confirmationFee) || confirmationFee)).toFixed(2)} {params.currency || 'JMD'}</span></p>
                        <p className="mb-2"><strong className="text-success">No amount due at pickup</strong></p>
                      </>
                    ) : (
                      <>
                        <p className="mb-2"><strong>Confirmation Fee Paid:</strong> <span className="fw-bold">${(parseFloat(transactionData?.amountPaid) || parseFloat(transactionData?.total) || parseFloat(params.total) || 0).toFixed(2)} {params.currency || 'JMD'}</span></p>
                        <p className="mb-2"><strong>Order Subtotal:</strong> <span className="fw-bold">${(Number(transactionData?.orderSubtotal) || 0).toFixed(2)}</span> <span className="text-muted">(Due at pickup)</span></p>
                        <p className="mb-2"><strong className="text-warning">Amount Due at Pickup: ${(Number(transactionData?.orderSubtotal) || 0).toFixed(2)}</strong></p>
                      </>
                    )}
                  </div>
                  <div className="col-md-6">
                    <p className="mb-2"><strong>Payment Gateway:</strong> <span className="text-break">{transactionData?.paymentGateway || paymentGateway}</span></p>
                    <p className="mb-2"><strong>Currency:</strong> {params.currency || 'JMD'}</p>
                    <p className="mb-2"><strong>Method:</strong> <span className="text-break">{transactionData?.paymentMethod || params.method || paymentMethod}</span></p>
                    {(transactionData?.deliveryOption || params.delivery_option) && (
                      <p className="mb-2"><strong>Delivery Option:</strong> <span className={`badge ${(transactionData?.deliveryOption || params.delivery_option) === 'delivery' ? 'bg-info' : 'bg-secondary'}`}>{transactionData?.deliveryOption || params.delivery_option}</span></p>
                    )}
                    {(transactionData?.deliveryAddress || params.delivery_address) && (
                      <p className="mb-2"><strong>Delivery Address:</strong> <span className="text-break">{transactionData?.deliveryAddress || params.delivery_address}</span></p>
                    )}
                    {(transactionData?.deliveryPhone || params.delivery_phone) && (
                      <p className="mb-2"><strong>Delivery Phone:</strong> <span className="text-break">{transactionData?.deliveryPhone || params.delivery_phone}</span></p>
                    )}
                  </div>
                </div>
                
                {/* FCM Notification Permission for successful payments - skip on iOS Safari (limited support, can cause blank screen) */}
                {isSuccess && !isIOSOrMobileSafari() && (
                  <div className="notification-permission-wrapper" style={{ marginBottom: '1rem' }}>
                    <NotificationPermission />
                  </div>
                )}
                
                <div className="mt-4">
                  <div className="row g-2 g-md-3 justify-content-center">
                    <div className="col-12 col-md-4">
                      <Button 
                        variant="primary" 
                        onClick={() => navigate('/')} 
                        className="w-100 py-2 py-md-3"
                        size="lg"
                        style={{
                          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-home me-2"></i>
                        <span className="d-none d-sm-inline">Return Home</span>
                        <span className="d-sm-none">Home</span>
                      </Button>
                    </div>
                    
                    {cartTransactionId && (
                      <div className="col-12 col-md-4">
                        <Button
                          variant="success"
                          onClick={() => navigate(`/order-status/${cartTransactionId}`)}
                          className="w-100 py-2 py-md-3"
                          size="lg"
                          style={{
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-eye me-2"></i>
                          <span className="d-none d-sm-inline">View Order</span>
                          <span className="d-sm-none">View</span>
                        </Button>
                      </div>
                    )}
                    
                    <div className="col-12 col-md-4">
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/carts')}
                        className="w-100 py-2 py-md-3"
                        size="lg"
                        style={{
                          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-shopping-cart me-2"></i>
                        <span className="d-none d-sm-inline">Back to Carts</span>
                        <span className="d-sm-none">Carts</span>
                      </Button>
                    </div>

                    {/* Print Receipt Button */}
                    {transactionData && (
                      <div className="col-12 col-md-4">
                        <Button 
                          variant="info" 
                          onClick={generateReceipt}
                          className="w-100 py-2 py-md-3"
                          size="lg"
                          style={{
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-print me-2"></i>
                          <span className="d-none d-sm-inline">Print Receipt</span>
                          <span className="d-sm-none">Print</span>
                        </Button>
                      </div>
                    )}
                  
                  {/* Show "Try Again" button for failed payments (but not for invalid cart status) */}
                  {!isSuccess && !invalidCartStatus && (
                      <div className="col-12 col-md-6 col-lg-4">
                    <Button 
                      variant="warning" 
                      onClick={() => {
                        // Navigate to CheckoutPage with the correct parameters
                        logger.debug('[WipayResponsePage] Try Again clicked - transactionData:', transactionData);
                        logger.debug('[WipayResponsePage] supermarketId:', transactionData?.supermarketId);
                        logger.debug('[WipayResponsePage] cartInstanceId:', transactionData?.cartInstanceId);
                        
                        if (transactionData?.supermarketId && transactionData?.cartInstanceId) {
                          logger.debug('[WipayResponsePage] Navigating to checkout with params:', {
                            supermarketId: transactionData.supermarketId,
                            cartInstanceId: transactionData.cartInstanceId
                          });
                          navigate(`/checkout/${transactionData.supermarketId}/${transactionData.cartInstanceId}`);
                        } else {
                          // Fallback to carts page if we don't have the required data
                          logger.warn('[WipayResponsePage] Missing supermarketId or cartInstanceId for checkout navigation, falling back to carts page');
                          logger.warn('[WipayResponsePage] Available transactionData keys:', transactionData ? Object.keys(transactionData) : 'null');
                          navigate('/carts');
                        }
                      }} 
                          className="w-100 py-2 py-md-3"
                          size="lg"
                          style={{
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                            fontWeight: '500'
                          }}
                    >
                          <i className="fas fa-redo me-2"></i>
                          <span className="d-none d-sm-inline">Try Payment Again</span>
                          <span className="d-sm-none">Try Again</span>
                    </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional help text for failed payments */}
                  {!isSuccess && (
                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        If you continue to experience issues, please contact support or try a different payment method.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </Layout>
  );
}